/* src/modules/it/components/ITReporting.js */
import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { 
    ChartBarIcon, ClipboardListIcon, UserIcon, 
    ClockIcon, ChevronDownIcon, SearchIcon,
    ChevronLeftIcon, ChevronRightIcon, EyeIcon, ArrowLeftIcon
} from '../../../components/icons/Icons';

export default function ITReporting({ appId }) {
    const [activeReport, setActiveReport] = useState('inventory_summary');
    const [assets, setAssets] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [users, setUsers] = useState({});
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState('30'); 

    // --- USER REPORT STATE ---
    const [userSearch, setUserSearch] = useState('');
    const [userSort, setUserSort] = useState({ key: 'name', direction: 'asc' });
    const [userPage, setUserPage] = useState(1);
    
    // --- MODAL STATES ---
    const [selectedUserForDetail, setSelectedUserForDetail] = useState(null); // Level 1: List of assets
    const [focusedAsset, setFocusedAsset] = useState(null); // Level 2: Specific asset detail

    // Fetch Data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // 1. Fetch Users
                const usersRef = collection(db, `artifacts/${appId}/users`);
                const usersSnap = await getDocs(query(usersRef, where("status", "==", "Approved")));
                const userMap = {};
                usersSnap.docs.forEach(doc => {
                    userMap[doc.id] = doc.data(); 
                });
                setUsers(userMap);

                // 2. Fetch Assets
                const assetsRef = collection(db, `artifacts/${appId}/it_assets`);
                const assetsSnap = await getDocs(assetsRef);
                setAssets(assetsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

                // 3. Fetch Tickets
                const ticketsRef = collection(db, `artifacts/${appId}/it_requests`);
                const ticketsSnap = await getDocs(query(ticketsRef, orderBy('createdAt', 'desc')));
                const ticketsData = ticketsSnap.docs.map(d => ({
                    ...d.data(),
                    createdAtDate: d.data().createdAt?.toDate() || new Date(),
                    updatedAtDate: d.data().updatedAt?.toDate() || new Date()
                }));
                setTickets(ticketsData);

            } catch (error) {
                console.error("Error fetching report data:", error);
            }
            setLoading(false);
        };
        fetchData();
    }, [appId]);

    // --- DATA PROCESSING ---

    // 1. Inventory Summary
    const inventoryStats = useMemo(() => {
        const stats = {};
        assets.forEach(asset => {
            const cat = asset.category || 'Unknown';
            if (!stats[cat]) stats[cat] = { total: 0, active: 0, inStock: 0, retired: 0 };
            
            stats[cat].total++;
            if (['Active', 'Assigned'].includes(asset.status)) stats[cat].active++;
            else if (asset.status === 'In Stock') stats[cat].inStock++;
            else if (['Retired', 'Lost/Stolen'].includes(asset.status)) stats[cat].retired++;
        });
        return Object.entries(stats).sort((a, b) => b[1].total - a[1].total);
    }, [assets]);

    // 2. User Allocations
    const userHoldingsList = useMemo(() => {
        const holdingsMap = {};

        assets.forEach(asset => {
            if (asset.assignedToUserId && ['Active', 'Assigned'].includes(asset.status)) {
                const uid = asset.assignedToUserId;
                if (!holdingsMap[uid]) {
                    const directoryUser = users[uid] || {};
                    holdingsMap[uid] = {
                        userId: uid,
                        name: directoryUser.name || asset.assignedToName || 'Unknown',
                        email: directoryUser.email || asset.assignedToEmail || '',
                        department: directoryUser.department || 'Unassigned',
                        assets: [],
                        count: 0,
                        categorySummary: {} 
                    };
                }
                
                holdingsMap[uid].assets.push(asset);
                holdingsMap[uid].count++;

                const cat = asset.category || 'Other';
                if (!holdingsMap[uid].categorySummary[cat]) {
                    holdingsMap[uid].categorySummary[cat] = 0;
                }
                holdingsMap[uid].categorySummary[cat]++;
            }
        });

        let list = Object.values(holdingsMap);

        if (userSearch) {
            const q = userSearch.toLowerCase();
            list = list.filter(u => 
                u.name.toLowerCase().includes(q) || 
                u.department.toLowerCase().includes(q)
            );
        }

        list.sort((a, b) => {
            const valA = a[userSort.key];
            const valB = b[userSort.key];
            if (valA < valB) return userSort.direction === 'asc' ? -1 : 1;
            if (valA > valB) return userSort.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return list;
    }, [assets, users, userSearch, userSort]);

    const ITEMS_PER_PAGE = 10;
    const totalUserPages = Math.ceil(userHoldingsList.length / ITEMS_PER_PAGE);
    const paginatedUsers = userHoldingsList.slice((userPage - 1) * ITEMS_PER_PAGE, userPage * ITEMS_PER_PAGE);

    const handleUserSort = (key) => {
        setUserSort(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // 3. Ticket Stats
    const ticketStats = useMemo(() => {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - parseInt(dateRange));
        const filtered = tickets.filter(t => dateRange === 'all' || t.createdAtDate >= cutoffDate);
        const counts = { total: 0, open: 0, resolved: 0, byType: {} };
        filtered.forEach(t => {
            counts.total++;
            if (['Open', 'In Progress'].includes(t.status)) counts.open++;
            if (['Resolved', 'Closed'].includes(t.status)) counts.resolved++;
            if (!counts.byType[t.type]) counts.byType[t.type] = { total: 0, open: 0, closed: 0 };
            counts.byType[t.type].total++;
            if (['Open', 'In Progress'].includes(t.status)) counts.byType[t.type].open++;
            else counts.byType[t.type].closed++;
        });
        return { counts, filtered };
    }, [tickets, dateRange]);

    // 4. Resolution Stats
    const resolutionStats = useMemo(() => {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - parseInt(dateRange));
        const closedTickets = tickets.filter(t => 
            (dateRange === 'all' || t.createdAtDate >= cutoffDate) &&
            ['Resolved', 'Closed'].includes(t.status) && t.createdAt && t.updatedAt
        );
        let totalTimeMs = 0;
        const distribution = { '< 1 Day': 0, '1-3 Days': 0, '3-7 Days': 0, '> 7 Days': 0 };
        closedTickets.forEach(t => {
            const diffMs = t.updatedAtDate - t.createdAtDate;
            totalTimeMs += diffMs;
            const diffDays = diffMs / (1000 * 60 * 60 * 24);
            if (diffDays < 1) distribution['< 1 Day']++;
            else if (diffDays <= 3) distribution['1-3 Days']++;
            else if (diffDays <= 7) distribution['3-7 Days']++;
            else distribution['> 7 Days']++;
        });
        const avgDays = closedTickets.length > 0 ? (totalTimeMs / closedTickets.length / (1000 * 60 * 60 * 24)).toFixed(1) : 0;
        return { avgDays, distribution, count: closedTickets.length };
    }, [tickets, dateRange]);


    if (loading) return <div className="p-12 text-center text-gray-400">Generating reports...</div>;

    return (
        <div className="space-y-6 animate-fadeIn">
            {/* Report Selector Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <ReportCard 
                    id="inventory_summary" 
                    title="Stock Overview" 
                    subtitle="Assets by Category & Status"
                    icon={<ChartBarIcon className="w-6 h-6"/>}
                    active={activeReport}
                    onClick={setActiveReport}
                />
                <ReportCard 
                    id="user_allocation" 
                    title="User Holdings" 
                    subtitle="Assets Assigned to Staff"
                    icon={<UserIcon className="w-6 h-6"/>} 
                    active={activeReport}
                    onClick={setActiveReport}
                />
                <ReportCard 
                    id="ticket_volume" 
                    title="Request Volume" 
                    subtitle="Tickets Raised & Status"
                    icon={<ClipboardListIcon className="w-6 h-6"/>}
                    active={activeReport}
                    onClick={setActiveReport}
                />
                <ReportCard 
                    id="resolution_time" 
                    title="Resolution Time" 
                    subtitle="Speed of Support Closure"
                    icon={<ClockIcon className="w-6 h-6"/>}
                    active={activeReport}
                    onClick={setActiveReport}
                />
            </div>

            {/* Date Filter */}
            {['ticket_volume', 'resolution_time'].includes(activeReport) && (
                <div className="flex justify-end">
                    <div className="relative">
                        <select 
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                            className="appearance-none bg-white border border-gray-300 text-gray-700 py-2 pl-4 pr-10 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#20BEC6]"
                        >
                            <option value="7">Last 7 Days</option>
                            <option value="30">Last 30 Days</option>
                            <option value="90">Last 3 Months</option>
                            <option value="all">All Time</option>
                        </select>
                        <ChevronDownIcon className="w-4 h-4 absolute right-3 top-3 text-gray-400 pointer-events-none"/>
                    </div>
                </div>
            )}

            {/* --- REPORT CONTENT AREA --- */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 min-h-[400px]">
                
                {/* 1. Inventory Summary */}
                {activeReport === 'inventory_summary' && (
                    <div>
                        <h3 className="text-lg font-bold text-[#213368] mb-4">Inventory Stock Levels</h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                                    <tr>
                                        <th className="px-4 py-3 rounded-tl-lg">Category</th>
                                        <th className="px-4 py-3 text-center">Total Count</th>
                                        <th className="px-4 py-3 text-center text-green-600">Active / Assigned</th>
                                        <th className="px-4 py-3 text-center text-blue-600">In Stock</th>
                                        <th className="px-4 py-3 text-center text-gray-400 rounded-tr-lg">Retired / Lost</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {inventoryStats.map(([category, counts]) => (
                                        <tr key={category} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 font-medium text-[#213368]">{category}</td>
                                            <td className="px-4 py-3 text-center font-bold">{counts.total}</td>
                                            <td className="px-4 py-3 text-center bg-green-50 text-green-700 font-medium">{counts.active}</td>
                                            <td className="px-4 py-3 text-center bg-blue-50 text-blue-700 font-medium">{counts.inStock}</td>
                                            <td className="px-4 py-3 text-center text-gray-500">{counts.retired}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* 2. User Allocation */}
                {activeReport === 'user_allocation' && (
                    <div>
                        <div className="flex flex-col md:flex-row justify-between items-end md:items-center mb-6 gap-4">
                            <h3 className="text-lg font-bold text-[#213368]">Assets by User</h3>
                            <div className="relative w-full md:w-64">
                                <SearchIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                <input 
                                    type="text" 
                                    placeholder="Search users or dept..."
                                    value={userSearch}
                                    onChange={(e) => { setUserSearch(e.target.value); setUserPage(1); }}
                                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none"
                                />
                            </div>
                        </div>

                        <div className="overflow-x-auto border border-gray-200 rounded-lg">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                                    <tr>
                                        <th className="px-6 py-3 cursor-pointer hover:bg-gray-100 w-1/4" onClick={() => handleUserSort('name')}>
                                            User {userSort.key === 'name' && (userSort.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th className="px-6 py-3 w-1/2">
                                            Asset Breakdown
                                        </th>
                                        <th className="px-6 py-3 text-center cursor-pointer hover:bg-gray-100 w-1/6" onClick={() => handleUserSort('count')}>
                                            Total {userSort.key === 'count' && (userSort.direction === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th className="px-6 py-3 text-right"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {paginatedUsers.length === 0 ? (
                                        <tr><td colSpan="4" className="p-8 text-center text-gray-400">No users found with assigned assets.</td></tr>
                                    ) : (
                                        paginatedUsers.map((user) => (
                                            <tr key={user.userId} className="hover:bg-gray-50 transition cursor-pointer" onClick={() => { setSelectedUserForDetail(user); setFocusedAsset(null); }}>
                                                <td className="px-6 py-4">
                                                    <div className="font-bold text-[#213368]">{user.name}</div>
                                                    <div className="text-xs text-gray-500">{user.department}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-wrap gap-2">
                                                        {Object.entries(user.categorySummary).map(([cat, count]) => (
                                                            <span key={cat} className="inline-flex items-center px-2 py-1 rounded bg-blue-50 border border-blue-100 text-blue-700 text-xs font-medium">
                                                                {cat} 
                                                                <span className="ml-1.5 bg-white px-1.5 rounded text-blue-900 font-bold">{count}</span>
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <span className="font-bold text-gray-700">{user.count}</span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <ChevronRightIcon className="w-5 h-5 text-gray-400 inline-block"/>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {userHoldingsList.length > 0 && (
                            <div className="flex items-center justify-between p-4">
                                <span className="text-xs text-gray-500">
                                    Page {userPage} of {totalUserPages}
                                </span>
                                <div className="flex gap-2">
                                    <button onClick={() => setUserPage(c => Math.max(1, c - 1))} disabled={userPage === 1} className="p-1 rounded bg-white border disabled:opacity-50"><ChevronLeftIcon className="w-4 h-4"/></button>
                                    <button onClick={() => setUserPage(c => Math.min(totalUserPages, c + 1))} disabled={userPage === totalUserPages} className="p-1 rounded bg-white border disabled:opacity-50"><ChevronRightIcon className="w-4 h-4"/></button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* 3. Ticket Volume (Unchanged) */}
                {activeReport === 'ticket_volume' && (
                    <div>
                        <h3 className="text-lg font-bold text-[#213368] mb-6">Request Volume Analysis</h3>
                        <div className="grid grid-cols-3 gap-6 mb-8">
                            <StatBox label="Total Requests" value={ticketStats.counts.total} color="bg-gray-100 text-gray-800" />
                            <StatBox label="Open / In Progress" value={ticketStats.counts.open} color="bg-orange-50 text-orange-700" />
                            <StatBox label="Resolved / Closed" value={ticketStats.counts.resolved} color="bg-green-50 text-green-700" />
                        </div>
                        <h4 className="text-sm font-bold text-gray-500 uppercase mb-3">Breakdown by Type</h4>
                        <div className="overflow-x-auto border rounded-lg">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                                    <tr>
                                        <th className="px-4 py-3 text-left">Request Type</th>
                                        <th className="px-4 py-3 text-center">Total</th>
                                        <th className="px-4 py-3 text-center text-orange-600">Pending</th>
                                        <th className="px-4 py-3 text-center text-green-600">Completed</th>
                                        <th className="px-4 py-3 text-right">Completion Rate</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {Object.entries(ticketStats.counts.byType).map(([type, data]) => {
                                        const rate = data.total > 0 ? Math.round((data.closed / data.total) * 100) : 0;
                                        return (
                                            <tr key={type}>
                                                <td className="px-4 py-3 font-medium text-[#213368]">{type}</td>
                                                <td className="px-4 py-3 text-center">{data.total}</td>
                                                <td className="px-4 py-3 text-center bg-orange-50">{data.open}</td>
                                                <td className="px-4 py-3 text-center bg-green-50">{data.closed}</td>
                                                <td className="px-4 py-3 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                            <div className="h-full bg-green-500" style={{ width: `${rate}%` }}></div>
                                                        </div>
                                                        <span className="text-xs font-bold">{rate}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* 4. Resolution Time (Unchanged) */}
                {activeReport === 'resolution_time' && (
                    <div>
                        <h3 className="text-lg font-bold text-[#213368] mb-6">Resolution Speed (SLA)</h3>
                        <div className="flex items-center gap-6 mb-8 p-6 bg-blue-50 rounded-xl border border-blue-100">
                            <div className="p-4 bg-white rounded-full shadow-sm">
                                <ClockIcon className="w-8 h-8 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-blue-600 font-bold uppercase">Average Resolution Time</p>
                                <p className="text-3xl font-bold text-[#213368]">{resolutionStats.avgDays} Days</p>
                                <p className="text-xs text-gray-500 mt-1">Based on {resolutionStats.count} closed tickets in this period.</p>
                            </div>
                        </div>
                        <h4 className="text-sm font-bold text-gray-500 uppercase mb-4">Time Distribution</h4>
                        <div className="space-y-4">
                            {Object.entries(resolutionStats.distribution).map(([range, count]) => {
                                const percentage = resolutionStats.count > 0 ? (count / resolutionStats.count) * 100 : 0;
                                return (
                                    <div key={range}>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="font-medium text-gray-700">{range}</span>
                                            <span className="text-gray-500">{count} Tickets ({percentage.toFixed(0)}%)</span>
                                        </div>
                                        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                                            <div 
                                                className={`h-full rounded-full transition-all duration-500 ${range === '< 1 Day' ? 'bg-green-500' : range === '1-3 Days' ? 'bg-blue-500' : range === '3-7 Days' ? 'bg-orange-400' : 'bg-red-400'}`} 
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* --- MODAL WRAPPER --- */}
            {selectedUserForDetail && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden max-h-[85vh] flex flex-col transition-all">
                        
                        {/* --- LEVEL 1: LIST VIEW --- */}
                        {!focusedAsset ? (
                            <>
                                <div className="bg-[#213368] p-6 flex justify-between items-center text-white shrink-0">
                                    <div>
                                        <h3 className="font-bold text-xl">{selectedUserForDetail.name}</h3>
                                        <p className="text-sm text-blue-200 mt-1">{selectedUserForDetail.department} • {selectedUserForDetail.count} Items Assigned</p>
                                    </div>
                                    <button onClick={() => setSelectedUserForDetail(null)} className="hover:text-gray-300 bg-white/10 p-2 rounded-full"><EyeIcon className="w-5 h-5 text-white"/></button>
                                </div>
                                <div className="p-0 overflow-y-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-gray-50 text-xs text-gray-500 uppercase sticky top-0 border-b border-gray-200">
                                            <tr>
                                                <th className="px-6 py-3">Category</th>
                                                <th className="px-6 py-3">Asset Tag</th>
                                                <th className="px-6 py-3">Summary</th>
                                                <th className="px-6 py-3 text-right">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {selectedUserForDetail.assets.map((asset) => (
                                                <tr key={asset.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setFocusedAsset(asset)}>
                                                    <td className="px-6 py-4 font-medium text-[#213368]">{asset.category}</td>
                                                    <td className="px-6 py-4 font-mono text-xs text-gray-600"><span className="bg-gray-100 px-2 py-1 rounded">{asset.assetTag}</span></td>
                                                    <td className="px-6 py-4 text-gray-600">
                                                        {asset.category === 'License / SaaS' ? asset.url : asset.model}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <span className="text-blue-600 text-xs font-bold hover:underline">View Details →</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end shrink-0">
                                    <button onClick={() => setSelectedUserForDetail(null)} className="px-6 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium shadow-sm">Close</button>
                                </div>
                            </>
                        ) : (
                            /* --- LEVEL 2: DETAILED VIEW --- */
                            <>
                                <div className="bg-gray-50 border-b border-gray-200 p-4 flex items-center justify-between shrink-0">
                                    <button onClick={() => setFocusedAsset(null)} className="flex items-center text-gray-600 hover:text-[#213368] font-medium text-sm">
                                        <ArrowLeftIcon className="w-4 h-4 mr-2"/> Back to List
                                    </button>
                                    <span className="text-xs font-mono bg-white border px-2 py-1 rounded text-gray-500">{focusedAsset.assetTag}</span>
                                </div>
                                
                                <div className="p-8 overflow-y-auto bg-white">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="p-3 bg-blue-50 rounded-lg text-blue-600">
                                            {focusedAsset.category === 'License / SaaS' ? <ClipboardListIcon className="w-8 h-8"/> : <ChartBarIcon className="w-8 h-8"/>}
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-bold text-[#213368]">{focusedAsset.category}</h2>
                                            <p className="text-gray-500">{focusedAsset.vendor} {focusedAsset.model}</p>
                                        </div>
                                    </div>

                                    {/* Detail Grid - Read Only */}
                                    <div className="grid grid-cols-2 gap-6 mb-6">
                                        <DetailField label="Status" value={focusedAsset.status} />
                                        <DetailField label="Procurement Date" value={focusedAsset.procurementDate} />
                                        
                                        {focusedAsset.category === 'License / SaaS' ? (
                                            <>
                                                <DetailField label="Login URL" value={focusedAsset.url} isLink />
                                                <DetailField label="Username / Email" value={focusedAsset.username} />
                                                <DetailField label="License Key" value={focusedAsset.serialNumber} />
                                            </>
                                        ) : (
                                            <>
                                                <DetailField label="Serial Number" value={focusedAsset.serialNumber} />
                                                <DetailField label="Model Name" value={focusedAsset.model} />
                                                {focusedAsset.category.includes('Laptop') && (
                                                    <div className="col-span-2 bg-gray-50 p-4 rounded-lg border border-gray-100 grid grid-cols-3 gap-4 mt-2">
                                                        <DetailField label="CPU" value={focusedAsset.cpu} />
                                                        <DetailField label="RAM" value={focusedAsset.ram} />
                                                        <DetailField label="Storage" value={focusedAsset.storage} />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        
                                        <div className="col-span-2">
                                            <DetailField label="Description / Notes" value={focusedAsset.description} fullWidth />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// --- SUB-COMPONENTS ---
function DetailField({ label, value, isLink, fullWidth }) {
    return (
        <div className={fullWidth ? 'w-full' : ''}>
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">{label}</label>
            {isLink ? (
                <a href={value} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">{value || 'N/A'}</a>
            ) : (
                <div className="text-gray-800 font-medium break-words">{value || 'N/A'}</div>
            )}
        </div>
    );
}

function ReportCard({ id, title, subtitle, icon, active, onClick }) {
    return (
        <button 
            onClick={() => onClick(id)}
            className={`text-left p-4 rounded-xl border transition-all duration-200 ${
                active === id 
                    ? 'border-[#20BEC6] bg-blue-50 ring-1 ring-[#20BEC6]' 
                    : 'border-gray-200 bg-white hover:border-[#20BEC6] hover:shadow-sm'
            }`}
        >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                active === id ? 'bg-[#20BEC6] text-white' : 'bg-gray-100 text-gray-500'
            }`}>
                {icon}
            </div>
            <h4 className={`font-bold ${active === id ? 'text-[#213368]' : 'text-gray-700'}`}>{title}</h4>
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        </button>
    );
}

function StatBox({ label, value, color }) {
    return (
        <div className={`p-4 rounded-lg text-center ${color}`}>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs font-bold uppercase opacity-75">{label}</p>
        </div>
    );
}